// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
/**
 * Pure URL scheme-allowlisting used by the native bridge. Kept dependency-free
 * (no Capacitor imports) so it is unit-testable on its own and reusable.
 *
 * SECURITY: only these schemes may ever be handed to Capacitor `Browser.open`
 * or `window.open`. A vault item, share link, or other user-controlled value
 * could otherwise carry a `javascript:`, `data:`, `file:`, or `vbscript:` URL
 * that executes script or exfiltrates local content when "opened".
 */
export const SAFE_URL_SCHEMES = new Set(["http:", "https:", "mailto:"]);

/**
 * Resolve `url` against `baseUrl` and return it only if it uses a safe scheme.
 * Returns `null` for unparseable or disallowed-scheme URLs. Relative URLs (e.g.
 * "/docs") resolve against the base and are therefore allowed.
 */
export function toSafeExternalUrl(url: string, baseUrl: string): string | null {
  if (typeof url !== "string" || url.trim() === "") {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(url, baseUrl);
  } catch {
    return null;
  }
  if (!SAFE_URL_SCHEMES.has(parsed.protocol)) {
    return null;
  }
  return parsed.toString();
}
